import {
  CaretLeftFilled,
  CaretRightFilled,
  LeftOutlined,
} from "@ant-design/icons";
import { Button, Calendar } from "antd";
import { loadedInitSelector } from "appredux/selectors";
import dayjs from "dayjs";
import { useGetMSCalendarMonth } from "queries/meetingSchedule";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { cleanBlankValue, cn, timeToMinutes } from "utils/helper";
import ModalDetailScheduleDay from "./ModalDetailScheduleDay";
import Skeleton from "components/UI/skeleton";
import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty, sortBy } from "lodash-es";
import { useMSScheduleStatuses } from "queries/meetingSchedule";
import { filterCalendarMonthOnlyOverlapActive } from "components/apps/MeetingSchedule/AllSchedule/Table/scheduleRowFields";

const CalendarLayoutThirdStep = ({
  prev,
  isEdit,
  data: dataTemp,
  onChangeDataForm,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";
  const { data: scheduleStatuses = [] } = useMSScheduleStatuses(locale);
  const loadedInit = useSelector(loadedInitSelector);
  const calendarRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState(dayjs());
  const [dataDetail, setDataDetail] = useState(null);

  const getPayload = useMemo(() => {
    return cleanBlankValue({
      month: selectedValue.startOf("month").format("YYYY-MM-DD"),
      schedule_admin_id: dataTemp?.schedule_admin_id,
    });
  }, [selectedValue, dataTemp]);

  const { data: rawData, isLoading } = useGetMSCalendarMonth(getPayload, {
    enabled: loadedInit,
  });

  const data = useMemo(
    () => filterCalendarMonthOnlyOverlapActive(rawData, scheduleStatuses),
    [rawData, scheduleStatuses]
  );

  const nextMonthHandler = useCallback(() => {
    setSelectedValue(selectedValue.clone().add(1, "month"));
  }, [selectedValue]);

  const prevMonthHandler = useCallback(() => {
    setSelectedValue(selectedValue.clone().subtract(1, "month"));
  }, [selectedValue]);

  const headerRender = useCallback(
    ({ value }) => {
      const month = dayjs(value).locale(dayjs().locale()).format("MMMM");
      const year = dayjs(value).locale(dayjs().locale()).format("YYYY");
      return (
        <div className="border-gray-200 border border-b-0 flex justify-between text-xl items-center">
          <div className="p-1 cursor-pointer" onClick={prevMonthHandler}>
            <CaretLeftFilled />
          </div>
          <div className="font-semibold flex-1 text-center">
            {month} {year}
          </div>
          <div className="p-1 cursor-pointer" onClick={nextMonthHandler}>
            <CaretRightFilled />
          </div>
        </div>
      );
    },
    [nextMonthHandler, prevMonthHandler]
  );

  const onSelect = useCallback(
    (data) => {
      if (dataTemp.start_time && dataTemp.end_time && dataTemp.date) {
        setDataDetail({
          ...data,
        });
      } else {
        setDataDetail(data);
      }
    },
    [dataTemp]
  );
  // untuk cek apakah anotherSlot berada di dalam slot
  const checkWithIn = useCallback((slot, anotherSlot) => {
    const slotStart = timeToMinutes(slot.start_time);
    const slotEnd = timeToMinutes(slot.end_time);
    const anotherStart = timeToMinutes(anotherSlot.start_time);
    const anotherEnd = timeToMinutes(anotherSlot.end_time);

    return (
      anotherStart >= slotStart &&
      anotherEnd <= slotEnd &&
      !(anotherStart === slotStart && anotherEnd === slotEnd)
    );
  }, []);
  const processTimeSlots = useCallback((data) => {
    const mainStartTime = data.start_time;
    const mainEndTime = data.end_time;

    const children = [...data.children];

    children.sort((a, b) => {
      return a.start_time.localeCompare(b.start_time);
    });

    const result = [];

    let currentTime = mainStartTime;

    for (const child of children) {
      if (currentTime < child.start_time) {
        result.push({
          start_time: currentTime,
          end_time: child.start_time,
        });
      }

      result.push({
        start_time: child.start_time,
        end_time: child.end_time,
      });

      currentTime = child.end_time;
    }

    if (currentTime < mainEndTime) {
      result.push({
        start_time: currentTime,
        end_time: mainEndTime,
      });
    }

    return result;
  }, []);

  const generateHour = useMemo(() => {
    if (isEmpty(data)) return [];
    const temp = [];
    Array(24)
      .fill(0)
      .forEach((_, i) => {
        temp.push({
          start_time: dayjs(dayjs().startOf("day").hour(i)).format("HH:mm:ss"),
          end_time: dayjs(dayjs().startOf("day").hour(i))
            .add(i === 23 ? 59 : 60, "minute")
            .format("HH:mm:ss"),
          percentage: 100,
        });
      });
    return temp;
  }, [data]);

  const groupOverlapSlots = useCallback(
    (slots) => {
      const result = [];
      const processed = new Set();

      slots.forEach((slot, index) => {
        if (processed.has(index)) return;

        const slotWithChildren = { ...slot, children: [] };
        processed.add(index);

        slots.forEach((otherSlot, otherIndex) => {
          if (index !== otherIndex && !processed.has(otherIndex)) {
            if (checkWithIn(slot, otherSlot)) {
              slotWithChildren.children.push(otherSlot);
              processed.add(otherIndex);
            }
          }
        });

        result.push(slotWithChildren);
      });

      return result;
    },
    [checkWithIn]
  );

  const groupAvailableTimes = useCallback(
    (times, meetingSchedule) => {
      const clonedTimes = cloneDeep(times);

      const combineTimes = generateHour.map((slotTimeGenerate) => {
        let children = [];
        let isChildrenHasChildren = false;
        clonedTimes.forEach((availableSlot) => {
          if (checkWithIn(slotTimeGenerate, availableSlot)) {
            const findInMeetingSchedule = meetingSchedule.find(
              (meetingSlot) =>
                (meetingSlot.start_time === availableSlot.start_time &&
                  meetingSlot.end_time === availableSlot.end_time) ||
                checkWithIn(meetingSlot, availableSlot)
            );
            if (!findInMeetingSchedule) {
              isChildrenHasChildren = true;
            }
            children.push(availableSlot);
          }
        });

        if (children.length > 0) {
          if (isChildrenHasChildren) {
            children = groupOverlapSlots(children);
          }
          return {
            ...slotTimeGenerate,
            children: sortBy(children, "start_time"),
          };
        } else {
          return slotTimeGenerate;
        }
      });

      const realTime = combineTimes.map((item) => {
        if (item.children && item.children.length > 0) {
          const updateChildren = item.children.flatMap((i) => {
            if (i.children && i.children.length > 0) {
              return processTimeSlots(i);
            }
            return [i];
          });

          return {
            ...item,
            children: processTimeSlots({ ...item, children: updateChildren }),
          };
        } else {
          return item;
        }
      });

      const addPercentage = realTime.map((item) => {
        const startTime = timeToMinutes(item.start_time);
        const endTime =
          item.end_time === "23:59:00" ? 1440 : timeToMinutes(item.end_time);
        const duration = endTime - startTime;

        // Process main item
        const processedItem = {
          ...item,
          percentage: (duration / 60) * 100, // assuming 1 hours = 100%
        };

        if (item.children && Array.isArray(item.children)) {
          processedItem.children = item.children.map((child) => {
            const childStartTime = timeToMinutes(child.start_time);
            const childEndTime =
              child.end_time === "23:59:00"
                ? 1440
                : timeToMinutes(child.end_time);
            const childDuration = childEndTime - childStartTime;

            return {
              ...child,
              percentage: (childDuration / 60) * 100, // assuming 1 hours = 100%
            };
          });
        }

        return processedItem;
      });

      return addPercentage;
    },
    [checkWithIn, processTimeSlots, generateHour, groupOverlapSlots]
  );

  const checkOverlap = useCallback((slot, meeting) => {
    const slotStart = timeToMinutes(slot.start_time);
    const slotEnd = timeToMinutes(slot.end_time);
    const meetingStart = timeToMinutes(meeting.start_time);
    const meetingEnd = timeToMinutes(meeting.end_time);

    return {
      hasOverlap: meetingStart < slotEnd && meetingEnd > slotStart,
      type:
        meetingStart === slotStart && meetingEnd === slotEnd
          ? "exact"
          : meetingStart >= slotStart && meetingEnd <= slotEnd
          ? "within"
          : meetingStart <= slotStart && meetingEnd >= slotEnd
          ? "covers"
          : meetingStart < slotEnd && meetingEnd > slotEnd
          ? "overflow_end"
          : meetingStart < slotStart && meetingEnd > slotStart
          ? "overflow_start"
          : "partial",
    };
  }, []);

  const processAvailableTimes = useCallback(
    (formatDate) => {
      if (!data || !data[formatDate]) return null;
      const newDataAvailable = groupAvailableTimes(
        data[formatDate].available_times,
        data[formatDate].meeting_schedules
      );
      return newDataAvailable.map((item) => {
        if (
          item.children &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          const processedChildren = item.children.map((child) => {
            const hasExactMatchInAvailable = data[
              formatDate
            ].available_times.find(
              (schedule) =>
                schedule.start_time === child.start_time &&
                schedule.end_time === child.end_time
            );

            const hasExactMatch = data[formatDate].meeting_schedules.find(
              (schedule) =>
                schedule.start_time === child.start_time &&
                schedule.end_time === child.end_time
            );

            const hasOverlap = data[formatDate].meeting_schedules.find(
              (schedule) => {
                const overlapResult = checkOverlap(child, schedule);
                return overlapResult.hasOverlap;
              }
            );
            const hasOverlapAvailable = data[formatDate].available_times.find(
              (schedule) => {
                const overlapResult = checkOverlap(child, schedule);
                return overlapResult.hasOverlap;
              }
            );
            const meetingSchedules = hasExactMatch
              ? hasExactMatch
              : hasOverlap
              ? hasOverlap
              : null;
            const currentSchedule =
              formatDate === dataTemp.date &&
              ((dayjs(dataTemp.start_time, "HH:mm").format("HH:mm:ss") ===
                child.start_time &&
                dayjs(dataTemp.end_time, "HH:mm").format("HH:mm:ss") ===
                  child.end_time) ||
                checkWithIn(
                  {
                    start_time: dayjs(dataTemp.start_time, "HH:mm").format(
                      "HH:mm:ss"
                    ),
                    end_time: dayjs(dataTemp.end_time, "HH:mm").format(
                      "HH:mm:ss"
                    ),
                  },
                  child
                ));
            return {
              ...child,
              // isAvailable: dataTemp.schedule_admin_id
              //   ? !hasExactMatch && !hasOverlap
              //   : true,
              meeting_schedules_data: meetingSchedules,
              currentSchedule,
              status:
                !hasExactMatchInAvailable && !hasOverlapAvailable
                  ? "unavailable"
                  : !dataTemp.schedule_admin_id
                  ? "available"
                  : !hasExactMatch && !hasOverlap
                  ? "available"
                  : "booked",
            };
          });

          return {
            ...item,
            children: processedChildren,
          };
        } else {
          const hasInAvailableTimes = data[formatDate].available_times.find(
            (schedule) =>
              schedule.start_time === item.start_time &&
              schedule.end_time === item.end_time
          );
          const hasExactMatch = data[formatDate].meeting_schedules.find(
            (schedule) =>
              schedule.start_time === item.start_time &&
              schedule.end_time === item.end_time
          );

          const hasOverlap = data[formatDate].meeting_schedules.find(
            (schedule) => {
              const overlapResult = checkOverlap(item, schedule);
              return overlapResult.hasOverlap;
            }
          );
          const meetingSchedules = hasExactMatch
            ? hasExactMatch
            : hasOverlap
            ? hasOverlap
            : null;

          const currentSchedule =
            formatDate === dataTemp.date &&
            ((dayjs(dataTemp.start_time, "HH:mm").format("HH:mm:ss") ===
              item.start_time &&
              dayjs(dataTemp.end_time, "HH:mm").format("HH:mm:ss") ===
                item.end_time) ||
              checkWithIn(
                {
                  start_time: dayjs(dataTemp.start_time, "HH:mm").format(
                    "HH:mm:ss"
                  ),
                  end_time: dayjs(dataTemp.end_time, "HH:mm").format(
                    "HH:mm:ss"
                  ),
                },
                item
              ));
          return {
            ...item,
            // isAvailable: dataTemp.schedule_admin_id
            //   ? !hasExactMatch && !hasOverlap
            //   : true,
            meeting_schedules_data: meetingSchedules,
            currentSchedule,
            status: !hasInAvailableTimes
              ? "unavailable"
              : !dataTemp.schedule_admin_id
              ? "available"
              : !hasExactMatch && !hasOverlap
              ? "available"
              : "booked",
          };
        }
      });
    },
    [data, groupAvailableTimes, checkOverlap, dataTemp, checkWithIn]
  );

  const customCellRenderCalendar = useCallback(
    (current) => {
      const formatDate = dayjs(current).format("YYYY-MM-DD"); // tanggal yang diformat calendar
      const isToday = dayjs(current).isSame(dayjs(), "day"); // untuk cek apakah hari ini
      const date = dayjs(current).format("D"); // tanggal dalam format D atau 1, 2, 3
      const isThisMonth = dayjs(current).month() === selectedValue.month(); // untuk cek apakah bulan ini
      const isWeekendOrUnavailable = data?.[formatDate] // untuk cek apakah weekend atau unavailable
        ? data[formatDate].type === "weekend" ||
          isEmpty(data[formatDate].available_times)
          ? true
          : false
        : false;
      let availableTimes = processAvailableTimes(formatDate);

      const dataCell = {
        ...data?.[formatDate],
        available_times: availableTimes,
        date: formatDate,
      };

      return (
        <div
          className={cn(
            "p-1 hover:border-primary border-2 border-transparent",
            isWeekendOrUnavailable && "bg-[#F6f6f6]"
          )}
          onClick={() => {
            if (isThisMonth && !current.isBefore(dayjs().startOf("day"))) {
              onSelect(dataCell);
            }
          }}
        >
          <>
            <div
              className={cn(
                "font-semibold w-6 h-6 inline-block rounded-full  text-center leading-[24px] ",
                isToday && "bg-primary text-white"
              )}
            >
              {date}
            </div>
            <div className="h-28 mt-1 overflow-y-auto">
              {!isThisMonth ? null : (
                <div>
                  {!isEmpty(dataCell.available_times) ? (
                    <div className="w-full flex flex-wrap  text-center gap-y-1">
                      {dataCell.available_times.map((item, index) => {
                        if (!item.children && item.status !== "unavailable") {
                          return (
                            <div
                              key={index}
                              className={cn(
                                "p-1 text-xs flex-shrink text-white line-clamp-1 overflow-hidden",
                                item.currentSchedule
                                  ? "bg-purple-500"
                                  : item.status === "available"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              )}
                              style={{
                                width: `${item.percentage}%`,
                              }}
                            >
                              {dayjs(item.start_time, "HH:mm:ss").format(
                                "HH:mm"
                              )}{" "}
                              s/d{" "}
                              {dayjs(item.end_time, "HH:mm:ss").format("HH:mm")}
                            </div>
                          );
                        } else if (item.children) {
                          return (
                            <div
                              key={index}
                              className="text-xs flex-shrink relative w-full"
                              style={{
                                width: `${item.percentage}%`,
                              }}
                            >
                              <div className="flex w-full h-6">
                                {item.children.map((child, childIndex) => {
                                  return (
                                    <div
                                      key={childIndex}
                                      className={cn(
                                        child.currentSchedule
                                          ? "bg-purple-500"
                                          : child.status === "unavailable"
                                          ? " bg-gray-400 bg-[image:repeating-linear-gradient(315deg,rgba(255,255,255,1)_0,_rgba(255,255,255,1)_0.1px,transparent_0,_transparent_50%)] bg-[size:10px_10px] bg-fixed"
                                          : child.status === "available"
                                          ? "bg-green-500"
                                          : "bg-red-500"
                                      )}
                                      style={{
                                        width: `${child.percentage}%`,
                                        height: "100%",
                                      }}
                                    ></div>
                                  );
                                })}
                              </div>

                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="px-1 rounded text-xs text-white line-clamp-1 overflow-hidden">
                                  {dayjs(item.start_time, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}{" "}
                                  s/d{" "}
                                  {dayjs(item.end_time, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </>
        </div>
      );
    },
    [data, selectedValue, processAvailableTimes, onSelect]
  );
  const handleClickSlot = (data) => {
    onChangeDataForm({
      date: data.date,
      start_time: dayjs(data.start_time, "HH:mm:ss").format("HH:mm"),
      end_time: dayjs(data.end_time, "HH:mm:ss").format("HH:mm"),
      modeForm: "calendar",
    });
  };
  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <div
            className="border overflow-x-auto mt-3"
            ref={calendarRef}
            id="calendar-meeting-schedule-container"
          >
            <Calendar
              className="min-w-[900px]" // responsive
              disabledDate={(current) => {
                return (
                  current.month() !== selectedValue.month() ||
                  current.isBefore(dayjs().startOf("day"))
                );
              }}
              value={selectedValue}
              headerRender={headerRender}
              fullCellRender={customCellRenderCalendar}
            />
            <ModalDetailScheduleDay
              data={dataDetail}
              setDataDetail={setDataDetail}
              dataTemp={dataTemp}
              handleClickSlot={handleClickSlot}
            />
          </div>
          <div className=" mt-5  flex justify-between">
            <>
              {!isEdit && (
                <Button icon={<LeftOutlined />} onClick={prev}>
                  {t("meeting_schedule.previous")}
                </Button>
              )}
            </>
          </div>
        </>
      )}
    </>
  );
};

export default CalendarLayoutThirdStep;
